import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';

// Configure axios instance for YouTube service
const youtubeServiceClient = axios.create({
  baseURL: config.api.youtubeServiceUrl,
  timeout: 30000,
  headers: {}
});

// Add request interceptor to include auth token
youtubeServiceClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// On 401, attempt a token refresh and retry once before logging out
applyAuthRetryInterceptor(youtubeServiceClient);

// Proactively refresh token before any request if within 30-minute expiry window
applyTokenRefreshInterceptor(youtubeServiceClient);

export const youtubeService = {
  // Get MS Teams channels
  getTeamsChannels: async () => {
    const response = await youtubeServiceClient.get('/youtube/teams-channels');
    return response.data;
  },

  // Subscription management
  getAllSubscriptions: async () => {
    const response = await youtubeServiceClient.get('/youtube/subscriptions');
    return response.data;
  },

  getSubscription: async (subscriptionId) => {
    const response = await youtubeServiceClient.get(`/youtube/subscriptions/${subscriptionId}`);
    return response.data;
  },

  createSubscription: async (data) => {
    const response = await youtubeServiceClient.post('/youtube/subscriptions', data);
    return response.data;
  },

  updateSubscription: async (subscriptionId, data) => {
    const response = await youtubeServiceClient.put(`/youtube/subscriptions/${subscriptionId}`, data);
    return response.data;
  },

  deleteSubscription: async (subscriptionId) => {
    const response = await youtubeServiceClient.delete(`/youtube/subscriptions/${subscriptionId}`);
    return response.data;
  },

  // Pause a subscription
  pauseSubscription: async (subscriptionId) => {
    const response = await youtubeServiceClient.post(`/youtube/subscriptions/${subscriptionId}/pause`);
    return response.data;
  },

  // Resume a subscription
  resumeSubscription: async (subscriptionId) => {
    const response = await youtubeServiceClient.post(`/youtube/subscriptions/${subscriptionId}/resume`);
    return response.data;
  },

  // Resubscribe to a channel
  resubscribe: async (subscriptionId) => {
    const response = await youtubeServiceClient.post(`/youtube/subscriptions/${subscriptionId}/resubscribe`);
    return response.data;
  },

  // Backfill historical transcripts for a subscription
  backfillTranscripts: async (subscriptionId, { years = 2, maxVideos = 500 } = {}) => {
    const response = await youtubeServiceClient.post(
      `/youtube/subscriptions/${subscriptionId}/backfill-transcripts`,
      { years, max_videos: maxVideos },
    );
    return response.data;
  }
};

export default youtubeService;
